import React, { useState, useEffect, useCallback } from "react";
import Hero from "../../Components/UserSideComponents/Home/Hero";
import { useAuth } from "../../Components/Context/AuthContext";
import AuthHero from "../../Components/UserSideComponents/Home/AuthHero";
import Sale from "../../Components/UserSideComponents/Home/Sale";
import CallUs from "../../Components/UserSideComponents/Home/CallUs";
import Review from "../../Components/UserSideComponents/Home/Review";
import Qna from "../../Components/UserSideComponents/Home/Qna";
import Categories from "../../Components/UserSideComponents/Home/Categories";
import FeaturedVarieties from "../../Components/UserSideComponents/Home/FeaturedVarieties";
import TermsDeclarationModal from "../../Components/Common/TermsDeclarationModal";
import { useLockBodyScroll } from "../../Components/Hooks/useLockBodyScroll";
import Career from "../../Components/UserSideComponents/Home/CareerHomeSection";
import SupplierPenaltyWarningModal from "../../Components/UserSideComponents/SupplierPenalties/SupplierPenaltyWarningModal";
import axios from "../../Utils/axiosInstance";

const Home = () => {
  const { isAuthenticated, user, updateUser, fetchProfile } = useAuth();
  const [termsModalDismissed, setTermsModalDismissed] = useState(false);

  // Supplier Penalties Popup state
  const [supplierPenalties, setSupplierPenalties] = useState([]);
  const [penaltiesChecked, setPenaltiesChecked] = useState(false);
  const [penaltyModalDismissedSession, setPenaltyModalDismissedSession] =
    useState(() => {
      return (
        sessionStorage.getItem("supplier_penalty_modal_dismissed") === "true"
      );
    });

  const userId = user?._id || user?.id;
  const isSupplier = user?.role === "supplier";

  const fetchSupplierPenalties = useCallback(async () => {
    if (!isAuthenticated || !isSupplier || !userId) return;
    try {
      const res = await axios.get(
        `/penalties/user/${userId}?status=Pending&limit=50`
      );
      if (res.data?.success) {
        setSupplierPenalties(res.data.data || []);
      }
    } catch (err) {
      console.error("[Home] Failed to load supplier penalties:", err);
    } finally {
      setPenaltiesChecked(true);
    }
  }, [isAuthenticated, isSupplier, userId]);

  useEffect(() => {
    if (isAuthenticated && isSupplier) {
      fetchSupplierPenalties();
    }
  }, [isAuthenticated, isSupplier, fetchSupplierPenalties]);

  const showTermsModal =
    !termsModalDismissed &&
    isAuthenticated &&
    user &&
    user.terms_And_condition?.isRead === false;

  const handleTermsAccepted = () => {
    setTermsModalDismissed(true);
    updateUser((prev) => ({
      ...prev,
      terms_And_condition: {
        ...(prev?.terms_And_condition || {}),
        isRead: true,
        readAt: new Date().toISOString(),
      },
    }));
  };

  // Determine whether access is blocked
  const isAppAccessBlocked =
    user?.isAppAccessBlocked ||
    supplierPenalties.some((p) => p.isBlocking || p.isPastDue);

  // Show penalty warning modal if:
  // 1. Supplier has pending penalties
  // 2. AND Terms modal is not blocking
  // 3. AND either access is blocked (CANNOT dismiss) OR not dismissed in this session
  const showPenaltyModal =
    isAuthenticated &&
    isSupplier &&
    supplierPenalties.length > 0 &&
    !showTermsModal &&
    (isAppAccessBlocked || !penaltyModalDismissedSession);

  const handlePayLater = () => {
    if (isAppAccessBlocked) return; // Cannot pay later if blocked
    setPenaltyModalDismissedSession(true);
    sessionStorage.setItem("supplier_penalty_modal_dismissed", "true");
  };

  const handlePenaltyPaymentSuccess = async () => {
    // Re-fetch profile from backend to sync unblocked status
    fetchProfile && (await fetchProfile());
    await fetchSupplierPenalties();
  };

  return (
    <section>
      {isAuthenticated ? <AuthHero /> : <Hero />}
      <Sale />
      <Categories />
      <FeaturedVarieties />
      <CallUs />
      {/* <Review /> */}
      <Career />
      <Qna />

      <TermsDeclarationModal
        show={showTermsModal}
        onAccepted={handleTermsAccepted}
      />

      {/* Supplier Penalty Warning & On-the-Spot Settlement Modal */}
      <SupplierPenaltyWarningModal
        isOpen={showPenaltyModal}
        onClose={handlePayLater}
        onPayLater={handlePayLater}
        penalties={supplierPenalties}
        isAppAccessBlocked={isAppAccessBlocked}
        user={user}
        onPaymentSuccess={handlePenaltyPaymentSuccess}
      />
    </section>
  );
};

export default Home;